import cloneDeep from 'lodash/cloneDeep';
import mergeWith from 'lodash/mergeWith';

export enum LayoutMode {
	Freeform = 'freeform',
	FlexHorizontal = 'flexHorizontal',
	FlexVertical = 'flexVertical',
	Grid = 'grid'
}

export enum LayoutResizingMode {
	Hug = 'hug',
	Fill = 'fill',
	Fixed = 'fixed'
}
//
export enum LayoutStackingOrder {
	FirstOnTop = 'firstOnTop',
	LastOnTop = 'lastOnTop'
}

export enum LayoutWrapMode {
	None = 'none',
	Wrap = 'wrap'
}

export enum LayoutMainAlignment {
	Start = 'start',
	Center = 'center',
	End = 'end',
	SpaceBetween = 'space-between',
	SpaceAround = 'space-around',
	SpaceEvenly = 'space-evenly'
}

export enum LayoutCrossAlignment {
	Start = 'start',
	Center = 'center',
	End = 'end',
	Stretch = 'stretch',
	Baseline = 'baseline'
}

export enum FlexDirection {
	Reverse = 'reverse',
	Initial = 'initial',
}

export type LayoutGapValue = number | string | 'auto';

export interface LayoutPadding {
	top?: string;
	right?: string;
	bottom?: string;
	left?: string;
}

export interface LayoutSpacingConfig {
	gap?: LayoutGapValue;
	crossGap?: LayoutGapValue;
	padding?: LayoutPadding;
}

export interface LayoutAlignmentConfig {
	main?: LayoutMainAlignment;
	cross?: LayoutCrossAlignment;
	baseline?: boolean;
	stackingOrder?: LayoutStackingOrder;
	alignItems?: LayoutCrossAlignment;
	justifyItems?: LayoutCrossAlignment;
}

export interface LayoutResizingConfig {
	width: LayoutResizingMode;
	height: LayoutResizingMode;
	minWidth?: string;
	minHeight?: string;
	maxWidth?: string;
	maxHeight?: string;
}

export interface LayoutFlexConfig {
	wrap?: LayoutWrapMode;
	direction?: FlexDirection;
}

export type LayoutTrackType = 'auto' | 'fixed' | 'fr';

export interface LayoutTrackDefinition {
	type: LayoutTrackType;
	value?: string;
}

export interface LayoutGridChildSpan {
	rowStart?: number;
	rowEnd?: number;
	columnStart?: number;
	columnEnd?: number;
}

export interface LayoutGridConfig {
	columns: LayoutTrackDefinition[];
	rows: LayoutTrackDefinition[];
	columnGap?: LayoutGapValue;
	rowGap?: LayoutGapValue;
	spans?: Record<string, LayoutGridChildSpan>;
}

export interface LayoutGroupModel {
	mode: LayoutMode;
	spacing?: LayoutSpacingConfig;
	alignment?: LayoutAlignmentConfig;
	resizing?: LayoutResizingConfig;
	flex?: LayoutFlexConfig;
	grid?: LayoutGridConfig;
	ignoreFlowIds?: string[];
}

const ARRAY_REPLACER = (_targetValue: unknown, sourceValue: unknown) => {
	if (Array.isArray(sourceValue)) {
		return [...sourceValue];
	}
	return undefined;
};

const layoutGroupDefaults: LayoutGroupModel = {
	mode: LayoutMode.Freeform,
	spacing: {},
	alignment: {
		main: LayoutMainAlignment.Start,
		cross: LayoutCrossAlignment.Start,
		baseline: false,
		stackingOrder: LayoutStackingOrder.LastOnTop
	},
	resizing: {
		width: LayoutResizingMode.Fixed,
		height: LayoutResizingMode.Fixed
	},
	flex: {
		wrap: LayoutWrapMode.None
	},
	grid: {
		columns: [],
		rows: [],
		columnGap: null,
		rowGap: null,
		spans: {}
	},
	ignoreFlowIds: []
};

export function createLayoutGroupDefaults(
	overrides?: Partial<LayoutGroupModel>
): LayoutGroupModel {
	return mergeWith(cloneDeep(layoutGroupDefaults), overrides ?? {}, ARRAY_REPLACER);
}

export function mergeLayoutGroup(
	base: LayoutGroupModel,
	overrides?: Partial<LayoutGroupModel>
): LayoutGroupModel {
	return mergeWith(cloneDeep(base), overrides ?? {}, ARRAY_REPLACER);
}

export function normalizeLayoutGroup(
	input?: Partial<LayoutGroupModel> | null
): LayoutGroupModel {
	return mergeLayoutGroup(createLayoutGroupDefaults(), input ?? {});
}
